import React, { useState, useCallback } from "react";
import { useDeviceConfigEntries, useTrends, useSelectedDateTimeFilter, useTimeFilterFrom, useTimeFilterTo, useDevicesFilterList } from "../../../hooks/useReduxSelectors";
import { ResponsiveContainer, LineChart, Line, XAxis, Tooltip, YAxis, Legend, CartesianGrid } from "recharts/umd/Recharts";
// import { ResponsiveContainer, LineChart, Line, XAxis, Tooltip, YAxis, Legend, CartesianGrid } from "recharts";
import dayjs from "dayjs";
import { Box } from "@mui/material";

// Export for testing
export const CustomizedAxisTick = (props) => {
  const { x, y, payload, selectedDateTimeFilter, timeFilterFrom, timeFilterTo } = props;
  let showDate = false;
  let showTime = false;
  let dateFormat = "DD-MM-YYYY";;
  let timeFormat = "HH:mm";
  const timeSpan = selectedDateTimeFilter && selectedDateTimeFilter.toLowerCase();
  const customTimeRangeFromValue = timeFilterFrom;
  const customTimeRangeToValue = timeFilterTo;
  let distanceBetweenFromAndTo = customTimeRangeFromValue - customTimeRangeToValue; // NOTE! distanceBetweenFromAndTo is a negative value

  switch (timeSpan) {
    case "live":
      showTime = true;
      timeFormat = "HH:mm:ss";
      break;
    case "hour":
      showTime = true;
      timeFormat = "HH:mm:ss";
      if (distanceBetweenFromAndTo < -1) {
        timeFormat = "HH:mm";
      }
      if (customTimeRangeFromValue < -12) {
        showDate = true;
        dateFormat = "DD-MM";
      }
      break;
    case "day":
      showTime = true;
      showDate = true;
      dateFormat = "DD-MM";
      if (distanceBetweenFromAndTo < -10) {
        showTime = false;
      }
      break;
    case "week":
      showDate = true;
      showTime = true;
      dateFormat = "DD-MM";
      if (customTimeRangeFromValue < -1) {
        showTime = false;
        showDate = true;
      }
      if (customTimeRangeFromValue < -8) {
        dateFormat = "DD-MM-YYYY";
        showTime = false;
        showDate = true;
      }
      break;
    case "month":
      showDate = true;
      dateFormat = "DD-MM";
      if (customTimeRangeFromValue < -4) {
        dateFormat = "DD-MM-YYYY";
      }
      break;
    case "year":
      showDate = true;
      dateFormat = "MM-YYYY";
      break;
    default:
      showTime = true;
      showDate = true;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#fff">
        {showDate && (
          <tspan x="0" dy="1.0em">
            {dayjs(payload.value).format(dateFormat)}
          </tspan>
        )}
        {showTime && (
          <tspan x="0" dy="1.2em">
            {dayjs(payload.value).format(timeFormat)}
          </tspan>
        )}
      </text>
    </g>
  );
};

const customLegend = (props) => {
  const { payload } = props;
  return (
    <ul>
      {
        payload.map((entry, index) => (
          <li key={`item-${index}`} style={{ color: "white" }}>{entry.value}</li>
        ))
      }
    </ul>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    let timeFormat = "DD-MM-YYYY HH:mm:ss";
    const ulStyle = {
      listStyleType: "none",
      padding: "0",
      margin: "0",
    };

    const items = payload.map((entry, index) => {
      const liStyle = {
        color: entry.stroke,
      };
      return (
        <li key={index} style={liStyle}>
          {`${entry.name}: ${entry.value.toFixed(2)} ${entry.unit}`}
        </li>
      );
    });

    return (
      <Box sx={{ bgcolor: "background.paper", p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
        <p>{`${dayjs(label).format(timeFormat)}`}</p>
        <ul style={ulStyle}>{items}</ul>
      </Box>
    );
  }

  return null;
};

function isDiagnostic(devicesFilterList, chart, chartIndex) {
  const chartConfig = devicesFilterList[chartIndex];
  return (
    chartConfig &&
    chartConfig[chart.hwId] &&
    chartConfig[chart.hwId][chart.valueName] &&
    chartConfig[chart.hwId][chart.valueName].flag === "diagnostic"
  );
}

const TrendsLineChart = (props) => {

  const { chartData, dateDomain, chartIndex } = props;
  const selectedDateTimeFilter = useSelectedDateTimeFilter();
  const timeFilterFrom = useTimeFilterFrom();
  const timeFilterTo = useTimeFilterTo();
  const devicesFilterList = useDevicesFilterList() || {};
  const devicesEntries = useDeviceConfigEntries();
  const trendsData = useTrends();
  const trendsWithDeviceNames = chartData.map((object) => {
    let matchedDevice = devicesEntries.find(device => device.hwId === object.hwId);
    if (matchedDevice) {
      let readableValueName;
      if (matchedDevice.measurements.length > 0) { readableValueName = matchedDevice.measurements.find(measurement => measurement.valueName === object.valueName); }
      if (!readableValueName && (matchedDevice.diagnostics.length > 0)) { readableValueName = matchedDevice.diagnostics.find(diagnostic => diagnostic.valueName === object.valueName); }
      return { ...object, name: matchedDevice.name, readableValueName: readableValueName.name, };
    }
    return object;
  });

  const [selectedLine, setSelectedLine] = useState("");

  const measurementDomain = trendsData?.measurementMinMaxRange?.[chartIndex];
  const diagnosticsDomain = trendsData?.diagnosticsMinMaxRange?.[chartIndex];
  let diagnosticsMin = Number.MAX_VALUE;
  let diagnosticsMax = Number.NEGATIVE_INFINITY;

  let measurementMin = Number.MAX_VALUE;
  let measurementMax = Number.NEGATIVE_INFINITY;

  chartData.forEach((chart) => {
    chart.data.forEach((element) => {
      if (element.measurementValue === null || element.measurementValue === undefined || isNaN(element.measurementValue)) {
        return;
      }
      if (isDiagnostic(devicesFilterList, chart, chartIndex)) {
        diagnosticsMin = Math.min(diagnosticsMin, element.measurementValue);

        diagnosticsMax = Math.max(diagnosticsMax, element.measurementValue);
      } else {
        measurementMin = Math.min(measurementMin, element.measurementValue);

        measurementMax = Math.max(measurementMax, element.measurementValue);
      }
    });
  });

  // inflate limits 10%
  if (measurementMin === Number.MAX_VALUE) {
    measurementMin = -30;
  } else if (measurementMin >= 0) {
    measurementMin = Math.floor(measurementMin * 0.9) - 1;
  } else {
    measurementMin = Math.floor(measurementMin * 1.1);
  }

  if (measurementMax === Number.NEGATIVE_INFINITY) {
    measurementMax = 30;
  } else if (measurementMax >= 0) {
    measurementMax = Math.ceil(measurementMax * 1.1) + 1;
  } else {
    measurementMax = Math.ceil(measurementMax * 0.9);
  }

  if (diagnosticsMin === Number.MAX_VALUE) {
    diagnosticsMin = -30;
  } else if (diagnosticsMin >= 0) {
    diagnosticsMin = Math.floor(diagnosticsMin * 0.9) - 1;
  } else {
    diagnosticsMin = Math.floor(diagnosticsMin * 1.1);
  }

  if (diagnosticsMax === Number.NEGATIVE_INFINITY) {
    diagnosticsMax = 30;
  } else if (diagnosticsMax >= 0) {
    diagnosticsMax = Math.ceil(diagnosticsMax * 1.1) + 1;
  } else {
    diagnosticsMax = Math.ceil(diagnosticsMax * 0.9);
  }

  let domainLeft =
    (measurementDomain && measurementDomain.start !== undefined && measurementDomain.end !== undefined)
      ? [() => measurementDomain.start, () => measurementDomain.end]
      : [measurementMin, measurementMax];


  let domainRight =
    (diagnosticsDomain && diagnosticsDomain.start !== undefined && diagnosticsDomain.end !== undefined)
      ? [() => diagnosticsDomain.start, () => diagnosticsDomain.end]
      : [diagnosticsMin, diagnosticsMax];

  const legendMouseEnter = useCallback(
    (o) => {
      setSelectedLine(
        o.payload.data[0]?.hwId.concat(o.payload.data[0]?.valueName)
      );
    },
    [selectedLine]
  );
  const legendMouseLeave = useCallback(
    (o) => {
      setSelectedLine("");
    },
    [selectedLine]
  );

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 15,
        }}
      >
        {/* <CartesianGrid strokeDasharray="1 15" />  */}
        <XAxis
          tickLine={false}
          dataKey="seconds"
          stroke="#fff"
          allowDuplicatedCategory={false}
          allowDataOverflow
          type="number"
          domain={[dateDomain.start, dateDomain.end]}
          tickCount={5}
          tick={<CustomizedAxisTick selectedDateTimeFilter={selectedDateTimeFilter} timeFilterFrom={timeFilterFrom} timeFilterTo={timeFilterTo} />}
          tickFormatter={(unixTime) => {
            return dayjs(unixTime).format("DD.MM.YYYY HH:mm:ss");
          }}
          interval="preserveStartEnd"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          onMouseEnter={legendMouseEnter}
          onMouseLeave={legendMouseLeave}
          verticalAlign="top"
          iconType={"line"}
        // content={customLegend}
        // wrapperStyle={{ backgroundColor: "rgba(255, 0, 0, 0.5)" }}
        // layout={"vertical"}
        ></Legend>
        <YAxis
          tickLine={false}
          yAxisId="left"
          domain={domainLeft}
          // label={{ value: 'Measurement', angle: -90, position: 'insideLeft' }}
          orientation="left"
          stroke="#fff"
          dataKey="measurementValue"
          tickCount={5}
          type="number"
          interval="preserveStartEnd"
        />
        <YAxis
          tickLine={false}
          yAxisId="right"
          orientation="right"
          type="number"
          dataKey="measurementValue"
          domain={domainRight}
          tickCount={5}
          dot={false}
          stroke="#fff"
          interval="preserveStartEnd"
        />
        {trendsWithDeviceNames &&
          trendsWithDeviceNames.length > 0 &&
          trendsWithDeviceNames.map((chart, idx) => {
            const unit =
              devicesFilterList[chartIndex] &&
              devicesFilterList[chartIndex][chart.hwId] &&
              devicesFilterList[chartIndex][chart.hwId][chart.valueName].unit;
            const color =
              devicesFilterList &&
                devicesFilterList[chartIndex] &&
                devicesFilterList[chartIndex][chart.hwId] &&
                devicesFilterList[chartIndex][chart.hwId][chart.valueName]
                ? devicesFilterList[chartIndex][chart.hwId][chart.valueName].color
                : "green";

            const strokeWidth =
              selectedLine === chart.hwId.concat(chart.valueName) ? 3 : 1;
            if (isDiagnostic(devicesFilterList, chart, chartIndex)) {
              return (
                <Line
                  key={`chartLineDiagnostics${idx}`}
                  yAxisId="right"
                  type="monotone"
                  dataKey="measurementValue"
                  dot={false}
                  stroke={color}
                  strokeWidth={strokeWidth}
                  data={chart.data}
                  name={`${chart.readableValueName} (${chart.name})`}
                  isAnimationActive={false}
                  unit={unit ?? ""}
                />
              );
            } else {
              return (
                <Line
                  key={`chartLine${idx}`}
                  yAxisId="left"
                  type="monotone"
                  dataKey="measurementValue"
                  dot={false}
                  stroke={color}
                  strokeWidth={strokeWidth}
                  data={chart.data}
                  name={`${chart.readableValueName} (${chart.name})`}
                  isAnimationActive={false}
                  unit={unit ?? ""}
                />
              );
            }
          })}

      </LineChart>
    </ResponsiveContainer>
  );
};

export default TrendsLineChart;
